/**
 * Submit a prompt. Grouped-form Cells, pickers in sheets, and Telegram's BottomButton as the
 * only submit affordance. Example media is collected as URLs because R2 is not enabled yet
 * (see BLOCKERS.md) — the field accepts links today and becomes an upload tile when it is.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PromptType } from '@promptvault/shared';
import { PROMPT_TYPES } from '@promptvault/shared';
import { fetchTaxonomy, submitPrompt } from '../api/taxonomy';
import { BottomSheet, SheetOptionGroup } from '../components/BottomSheet';
import { Icon } from '../components/Icon';
import { Padded, Screen } from '../components/Screen';
import { Placeholder } from '../components/feedback';
import { Button, Cell, Section } from '../components/primitives';
import { compactNumber, localizeDigits } from '../lib/format';
import { promptTypeLabel, useI18n } from '../lib/i18n';
import { hapticNotify, useMainButton } from '../hooks/useTg';

type Picker = 'type' | 'model' | null;

const MAX_BODY = 20000;
const MAX_EXAMPLES = 3;

export function Submit() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<PromptType>('image');
  const [model, setModel] = useState<string | null>(null);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [instructions, setInstructions] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [examples, setExamples] = useState<string[]>([]);
  const [exampleDraft, setExampleDraft] = useState('');
  const [picker, setPicker] = useState<Picker>(null);
  const [done, setDone] = useState<'ok' | 'duplicate' | null>(null);

  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: fetchTaxonomy,
    staleTime: 3_600_000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      submitPrompt({
        title: title.trim(),
        body: body.trim(),
        type,
        models: model ? [model] : undefined,
        tags: tags.length > 0 ? tags : undefined,
        negativePrompt: negativePrompt.trim() || undefined,
        instructions: instructions.trim() || undefined,
        sourceUrl: sourceUrl.trim() || undefined,
        exampleUrls: examples.length > 0 ? examples : undefined,
      }),
    onSuccess: (res) => {
      hapticNotify(res.status === 'duplicate' ? 'warning' : 'success');
      setDone(res.status === 'duplicate' ? 'duplicate' : 'ok');
      window.scrollTo(0, 0);
    },
    onError: () => hapticNotify('error'),
  });

  const canSubmit =
    title.trim().length >= 3 && body.trim().length >= 3 && !mutation.isPending && !done;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    mutation.mutate();
  }, [canSubmit, mutation]);

  useMainButton({
    text: t.submitCta,
    onClick: handleSubmit,
    visible: !done,
    disabled: !canSubmit,
    progress: mutation.isPending,
  });

  const addTag = () => {
    const value = tagDraft.trim().replace(/^#/, '').toLowerCase();
    if (!value || tags.includes(value) || tags.length >= 20) return;
    setTags((prev) => [...prev, value]);
    setTagDraft('');
  };

  const addExample = () => {
    const value = exampleDraft.trim();
    if (!value || examples.length >= MAX_EXAMPLES) return;
    try {
      // Reject anything that isn't a real absolute URL before it reaches the API.
      const parsed = new URL(value);
      if (!parsed.protocol.startsWith('http')) return;
    } catch {
      return;
    }
    setExamples((prev) => [...prev, value]);
    setExampleDraft('');
  };

  if (done) {
    return (
      <Screen>
        <Placeholder
          icon="check"
          title={done === 'duplicate' ? t.submitDuplicate : t.submitDone}
          description={done === 'duplicate' ? undefined : t.submitDoneSub}
          action={{ label: t.browsePrompts, onClick: () => navigate('/') }}
        />
      </Screen>
    );
  }

  return (
    <Screen hasBottomButton chrome="page">
      <Padded className="pt-4">
        <Section header={t.details}>
          <div className="hairline-b px-3.5 py-2.5">
            <label htmlFor="pv-title" className="block pb-0.5 text-caption text-hint">
              {t.titleLabel}
            </label>
            <input
              id="pv-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.titlePh}
              maxLength={200}
              className="h-7 w-full border-0 bg-transparent text-body text-text outline-none placeholder:text-hint"
            />
          </div>
          <div className="px-3.5 py-2.5">
            <div className="flex items-baseline justify-between gap-2 pb-1">
              <label htmlFor="pv-body" className="text-caption text-hint">
                {t.promptLabel}
              </label>
              <span className="tnum text-caption text-subtitle">
                {localizeDigits(body.length, lang)} / {localizeDigits(MAX_BODY, lang)}
              </span>
            </div>
            <textarea
              id="pv-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t.promptPh}
              rows={5}
              maxLength={MAX_BODY}
              className="block w-full resize-none border-0 bg-transparent text-body leading-relaxed text-text outline-none placeholder:text-hint"
            />
          </div>
        </Section>
      </Padded>

      <Padded className="pt-5">
        <Section header={t.classify}>
          <Cell
            title={t.type}
            value={promptTypeLabel(type, lang)}
            chevron
            onClick={() => setPicker('type')}
          />
          <Cell
            title={t.model}
            value={taxonomy?.models.find((m) => m.slug === model)?.name ?? t.all}
            chevron
            onClick={() => setPicker('model')}
          />
          <div className="px-3.5 py-2.5" data-testid="tags-row">
            <span className="block pb-1.5 text-caption text-hint">{t.tagsLabel}</span>
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setTags((prev) => prev.filter((x) => x !== tag))}
                  className="flex items-center gap-1.5 rounded-full bg-accent-tint px-2.5 py-1.5 text-footnote text-accent"
                >
                  #{tag}
                  <Icon name="close" size={11} strokeWidth={3} />
                </button>
              ))}
              <input
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                onBlur={addTag}
                placeholder={t.addTag}
                maxLength={40}
                className="min-w-[80px] flex-1 border-0 bg-transparent text-footnote text-text outline-none placeholder:text-hint"
              />
            </div>
          </div>
          <div className="hairline-t px-3.5 py-2.5">
            <label htmlFor="pv-neg" className="block pb-0.5 text-caption text-hint">
              {t.negPrompt}
            </label>
            <input
              id="pv-neg"
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder={t.negPromptPh}
              maxLength={4000}
              className="h-7 w-full border-0 bg-transparent text-body text-text outline-none placeholder:text-hint"
            />
          </div>
          <div className="px-3.5 py-2.5">
            <label htmlFor="pv-how" className="block pb-0.5 text-caption text-hint">
              {t.howToUse}
            </label>
            <input
              id="pv-how"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={t.instructionsPh}
              maxLength={4000}
              className="h-7 w-full border-0 bg-transparent text-body text-text outline-none placeholder:text-hint"
            />
          </div>
        </Section>
      </Padded>

      <Padded className="pt-5">
        <Section header={t.examples} footer={t.submitHint}>
          <div className="p-2.5">
            <div className="grid grid-cols-3 gap-2">
              {examples.map((url, i) => (
                <div
                  key={url}
                  className="relative overflow-hidden rounded-thumb bg-media-placeholder"
                  style={{ aspectRatio: '1 / 1' }}
                >
                  <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
                  <button
                    type="button"
                    aria-label={t.removeExample(compactNumber(i + 1, lang))}
                    onClick={() => setExamples((prev) => prev.filter((x) => x !== url))}
                    className="absolute end-1 top-1 flex h-5 w-5 items-center justify-center rounded-full text-on-media"
                    style={{ background: 'var(--pv-media-scrim-strong)' }}
                  >
                    <Icon name="close" size={11} strokeWidth={3} />
                  </button>
                </div>
              ))}
              {examples.length < MAX_EXAMPLES && (
                <div
                  className="flex flex-col items-center justify-center gap-1 rounded-thumb border border-dashed border-accent-tint-strong text-accent"
                  style={{ aspectRatio: '1 / 1' }}
                >
                  <Icon name="image" size={21} />
                  <span className="text-caption2">{t.add}</span>
                </div>
              )}
            </div>
            {examples.length < MAX_EXAMPLES && (
              <div className="flex gap-2 pt-2.5">
                <input
                  value={exampleDraft}
                  onChange={(e) => setExampleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addExample();
                    }
                  }}
                  placeholder="https://…"
                  inputMode="url"
                  /* A URL is not prose. Left in the RTL flow, bidi reorders `https://…` into
                     `…//:https` — the user is shown a mangled placeholder and, worse, watches
                     their own pasted link scramble as they type it. `text-start` keeps the field
                     itself anchored where the rest of the form is. */
                  dir="ltr"
                  className="min-w-0 flex-1 rounded-button bg-fill px-3 py-2 text-start text-footnote text-text outline-none placeholder:text-hint"
                />
                <Button variant="secondary" onClick={addExample}>
                  {t.add}
                </Button>
              </div>
            )}
          </div>
        </Section>
      </Padded>

      <Padded className="pb-6 pt-5">
        <Section header={t.sourceUrlLabel}>
          <div className="px-3.5 py-2.5">
            <input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder={t.sourceUrlPh}
              inputMode="url"
              maxLength={500}
              /* Same reason as the example URL above. */
              dir="ltr"
              className="h-7 w-full border-0 bg-transparent text-start text-body text-text outline-none placeholder:text-hint"
            />
          </div>
        </Section>
      </Padded>

      <BottomSheet
        open={picker !== null}
        onClose={() => setPicker(null)}
        title={picker === 'type' ? t.type : t.model}
        footer={
          <Button size="lg" className="w-full" onClick={() => setPicker(null)}>
            {t.done}
          </Button>
        }
      >
        {picker === 'type' ? (
          <SheetOptionGroup
            label={t.type}
            options={PROMPT_TYPES.map((ty) => ({
              value: ty,
              label: promptTypeLabel(ty, lang),
              selected: type === ty,
              onSelect: () => setType(ty),
            }))}
          />
        ) : (
          <SheetOptionGroup
            label={t.model}
            options={[
              {
                value: 'none',
                label: t.all,
                selected: model === null,
                onSelect: () => setModel(null),
              },
              ...(taxonomy?.models ?? []).map((m) => ({
                value: m.slug,
                label: m.name,
                selected: model === m.slug,
                onSelect: () => setModel(m.slug),
              })),
            ]}
          />
        )}
      </BottomSheet>
    </Screen>
  );
}
